import { Typography } from '@remarkable/ark-web';
import type { FleetDevice } from 'src/api/endpoints/cloudEnterprise.types';
import { useDeleteDevice } from 'src/api/queries/enterpriseAdmin';
import { Button, Divider, Modal, NotificationBox } from 'src/components';
import { DeviceIcon } from 'src/components/DeviceIcon';
import { DeviceName } from 'src/components/DeviceName';

export function DeviceActionTriggerUnpair({
  device,
  userEmail,
  userId,
  onSuccess,
}: {
  device: FleetDevice;
  userEmail: string | undefined;
  userId: string;
  onSuccess: () => void;
}) {
  const deleteDevice = useDeleteDevice();
  return (
    <Modal.Content title="Force device unpairing?">
      <div className="flex flex-col gap-y-24 lm:gap-y-40">
        <div>
          <Typography variant="body-lg" fontWeight="semibold" as="div">
            <DeviceIcon
              device={device}
              size={24}
              className="mr-6 inline align-sub"
            />
            <b>
              <DeviceName device={device} />
            </b>{' '}
            / <span className="whitespace-nowrap">{device.serialNumber}</span>
          </Typography>

          <Typography variant="body-lg" className="mt-8" as="div">
            (paired with {userEmail})
          </Typography>
        </div>
        <Divider />
        <div className="mx-auto flex max-w-ls flex-col gap-y-24 text-left lm:gap-y-32">
          <Modal.Description asChild>
            <Typography variant="body-sm">
              You are about to unpair this device and remove it from your admin
              overview.
              <br />
              <br />
              This will not delete data on the device, but it will stop it from
              syncing. The device can&apos;t be paired with any other account
              until it has been factory reset.
            </Typography>
          </Modal.Description>
        </div>
        <div className="flex justify-center gap-16">
          <Modal.Close asChild>
            <Button variant="secondary">Cancel</Button>
          </Modal.Close>

          <Button
            variant="primary"
            loading={deleteDevice.isPending}
            onClick={() => {
              deleteDevice.mutate(
                {
                  deviceId: device.serialNumber,
                  memberId: userId,
                },
                { onSuccess }
              );
            }}
          >
            Unpair device
          </Button>
        </div>
      </div>
      <NotificationBox error={deleteDevice.error} className="mt-16" />
    </Modal.Content>
  );
}
